import { EntityFormModal } from '@/components/EntityFormModal'
import { useSeededModalField } from '@/hooks/useSeededModalField'
import type { Firm } from '@/types/bundle'
import { buildNewFirm, deriveFirmId } from '../overrideUtils'

export interface NewOrDuplicateFirmModalProps {
  open: boolean
  onClose: () => void
  existingIds: string[]
  /** Present for 'duplicate', absent for '+ New' - drives title/prefill, same
   * pattern as document-types' NewOrDuplicateDocumentTypeModal. */
  source?: Firm
  onConfirm: (firmId: string, firm: Firm) => void
}

// Both "+ Add Firm" and per-firm "Duplicate" land on this same create form
// (PRD 06 AC9) - only a Name is entered; the firm id (the object key
// clients.json actually stores firms under) is auto-derived rather than
// typed, per the user's own confirmed decision.
export function NewOrDuplicateFirmModal({
  open,
  onClose,
  existingIds,
  source,
  onConfirm,
}: NewOrDuplicateFirmModalProps) {
  const openKey = open ? (source?.name ?? '__new__') : null
  const { value: name, setValue: setName, reset } = useSeededModalField(openKey, () =>
    source ? `Copy of ${source.name}` : '',
  )

  const trimmedName = name.trim()
  const firmId = trimmedName.length > 0 ? deriveFirmId(trimmedName, existingIds) : ''
  const isValid = trimmedName.length > 0

  function handleClose() {
    reset()
    onClose()
  }

  function handleConfirm() {
    if (!isValid) return
    onConfirm(firmId, buildNewFirm(trimmedName, source))
    handleClose()
  }

  return (
    <EntityFormModal
      open={open}
      entityLabel="Firm"
      isDuplicate={Boolean(source)}
      isValid={isValid}
      onClose={handleClose}
      onConfirm={handleConfirm}
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-meta font-medium text-text-secondary" htmlFor="new-firm-name">
            Firm name
          </label>
          <input
            id="new-firm-name"
            type="text"
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="e.g. ConveyancingHome"
            className="rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary outline-none placeholder:text-text-disabled"
          />
        </div>
        {firmId && <span className="text-meta text-text-secondary">Firm ID: {firmId}</span>}
        {source && (
          <p className="text-meta text-text-secondary">
            Enabled products, overrides, and output profile are copied from {source.name}. The API key and
            enabled status are never copied. This firm starts disabled with no key.
          </p>
        )}
      </div>
    </EntityFormModal>
  )
}
